import { useState } from 'react'
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom'
import { LayoutDashboard, Film, GitCommit, Wand2, Tags, ChevronLeft, ChevronRight, Users, ChevronDown, FolderOpen, Clapperboard, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ThemeToggle } from '@/components/theme/ThemeToggle'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'
import { LogOut } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useProjects, useProject } from '@/hooks/useProjects'
import { usePermissions } from '@/hooks/usePermissions'
import { useAnalytics } from '@/hooks/useAnalytics'
import { UsageBanner } from '@/components/usage/UsageBanner'

interface ProjectLayoutProps {
  children: React.ReactNode
  projectName?: string
}

export function ProjectLayout({ children, projectName }: ProjectLayoutProps) {
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { data: projects = [] } = useProjects()
  const { data: project } = useProject(id)
  const permissions = usePermissions(id, project?.owner_user_id)
  const { track } = useAnalytics()

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', href: `/projects/${id}/dashboard` },
    { icon: Film, label: 'Clips', href: `/projects/${id}/clips` },
    { icon: GitCommit, label: 'Commits', href: `/projects/${id}/commits` },
    { icon: Wand2, label: 'VFX', href: `/projects/${id}/vfx` },
    { icon: Package, label: 'Transcodes', href: `/projects/${id}/transcodes` },
  ]

  const settingsItems = [
    { icon: Users, label: 'Team', href: `/projects/${id}/team` },
    { icon: Tags, label: 'Tags', href: `/projects/${id}/tags` },
    // VFX Settings only visible to Admin and Editor (based on canEditProject permission)
    ...(permissions.canEditProject ? [{ icon: Clapperboard, label: 'VFX Settings', href: `/projects/${id}/vfx-settings` }] : []),
  ]

  const handleLogout = async () => {
    try {
      // Track logout before signing out
      track('auth_logout', {
        source: 'project_layout',
        project_id: id,
      })

      await signOut()
      toast.success('Logged out successfully')
      navigate('/login')
    } catch (error) {
      toast.error('Failed to log out')
    }
  }

  const handleSidebarToggle = () => {
    const newCollapsedState = !collapsed
    setCollapsed(newCollapsedState)

    track('sidebar_toggled', {
      project_id: id,
      collapsed: newCollapsedState,
    })
  }

  const handleNavigationClick = (label: string, href: string) => {
    track('navigation_clicked', {
      location: 'project_sidebar',
      navigation_item: label.toLowerCase(),
      destination: href,
      project_id: id,
    })
  }

  const handleProjectSwitch = (projectId: string) => {
    // Track project switch (no project names for privacy - could reveal unannounced films)
    track('project_switched', {
      from_project_id: id,
      to_project_id: projectId,
    })
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <nav
        className={cn(
          'relative border-r border-border bg-card transition-all duration-300 flex flex-col',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-border">
          {!collapsed && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 h-auto p-2 hover:bg-secondary">
                  <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
                    <span className="text-sm font-bold text-primary-foreground">
                      {projectName ? projectName.charAt(0).toUpperCase() : 'P'}
                    </span>
                  </div>
                  <span className="font-semibold text-lg">{projectName || 'Project'}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {projects.map((project) => (
                  <DropdownMenuItem key={project.id} asChild>
                    <Link
                      to={`/projects/${project.id}/dashboard`}
                      onClick={() => handleProjectSwitch(project.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-2 w-full">
                        <div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-primary">
                            {project.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="truncate">{project.name}</span>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                ))}
                {projects.length > 0 && <DropdownMenuSeparator />}
                <DropdownMenuItem asChild>
                  <Link to="/projects" className="cursor-pointer flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    <span>View All Projects</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSidebarToggle}
            className={cn('h-8 w-8', collapsed && 'mx-auto')}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation Items */}
        <div className="p-3 space-y-1 flex-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => handleNavigationClick(item.label, item.href)}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            )
          })}
        </div>

        {/* Settings Section */}
        <div className="border-t border-border">
          <div className="p-3 space-y-1">
            {settingsItems.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => handleNavigationClick(item.label, item.href)}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  )}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              )
            })}
          </div>
        </div>
      </nav>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between">
          <div className="flex-1" />
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <UsageBanner />
          {children}
        </main>
      </div>
    </div>
  )
}
